import { convertLocalTimeToUTC, convertUTCToLocalTime } from './timeUtils';

/**
 * TimeValue - Explicitly tracks time format (LOCAL or UTC)
 * 
 * This class helps prevent double conversion issues by making the
 * time format explicit rather than implicit.
 */
export class TimeValue {
  /**
   * Create a new TimeValue
   * @param {string} value - The time string (e.g., "13:30:00")
   * @param {string} format - Format of the time: "LOCAL" or "UTC"
   * @param {boolean} converted - Whether this value has been converted
   */
  constructor(value, format = 'LOCAL', converted = false) {
    this.value = value;          // The time string (e.g., "13:30:00")
    this.format = format;        // Explicit format: "LOCAL" or "UTC"
    this.converted = converted;  // Flag to track if conversion happened
    
    // Ensure proper time format (HH:MM:SS)
    if (this.value && !this.value.includes(':')) {
      this.value = `${this.value}:00`;
    }
    
    if (this.value && this.value.split(':').length === 2) {
      this.value = `${this.value}:00`;
    }
  }
  
  /**
   * Convert to UTC if not already in UTC format
   * @returns {TimeValue} New TimeValue instance in UTC format
   */
  toUTC() {
    if (this.format === 'UTC') {
      console.log(`⏰ Already UTC: ${this.value}`);
      return this; // Already UTC, don't convert
    }
    
    // Only perform conversion if we have a valid value
    if (!this.value) return new TimeValue('', 'UTC', true);
    
    console.log(`⏰ Converting explicit LOCAL time ${this.value} to UTC`);
    const utcValue = convertLocalTimeToUTC(this.value);
    console.log(`⏰ Conversion result: ${utcValue} (UTC)`);
    
    return new TimeValue(utcValue, 'UTC', true);
  }
  
  /**
   * Convert to LOCAL if not already in LOCAL format
   * @returns {TimeValue} New TimeValue instance in LOCAL format
   */
  toLocal() {
    if (this.format === 'LOCAL') {
      return this; // Already local, don't convert
    }
    
    // Only perform conversion if we have a valid value
    if (!this.value) return new TimeValue('', 'LOCAL', true);
    
    console.log(`🔄 Converting explicit UTC time ${this.value} to LOCAL`);
    const localValue = convertUTCToLocalTime(this.value);
    console.log(`✅ Explicit conversion result: ${localValue} (LOCAL)`);
    
    return new TimeValue(localValue, 'LOCAL', true);
  }
  
  /**
   * For API serialization
   * @returns {object} Serializable object
   */
  toJSON() {
    return {
      value: this.value,
      format: this.format,
      converted: this.converted
    };
  }
  
  /**
   * Get string value - for compatibility with existing code
   * @returns {string} The time string value
   */
  toString() {
    return this.value;
  }
  
  /**
   * Create a TimeValue from any input
   * @param {string|TimeValue} input - Input time value
   * @param {string} defaultFormat - Default format to use if input is string
   * @returns {TimeValue} New or existing TimeValue
   */
  static from(input, defaultFormat = 'LOCAL') {
    if (input instanceof TimeValue) {
      return input;
    }
    
    if (typeof input === 'string') {
      return new TimeValue(input, defaultFormat);
    }
    
    return new TimeValue('', defaultFormat);
  }
} 